import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import ModalPortal from './ModalPortal';
import {
    Mail,
    Send,
    Clock,
    Calendar,
    Settings,
    Users,
    CheckCircle,
    AlertCircle,
    Loader,
    Eye
} from 'lucide-react';

const ApiUsageReportControl = () => {
    const { currentWorkspace } = useWorkspace();
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [schedule, setSchedule] = useState({
        dailyHour: 8,
        dailyMinute: 0,
        weeklyHour: 9,
        weeklyMinute: 0,
        weeklyDay: 1 // Monday
    });
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isSavingSchedule, setIsSavingSchedule] = useState(false);

    // Update current time every second for live countdown (IST timezone)
    React.useEffect(() => {
        const timer = setInterval(() => {
            // Force IST timezone for consistent scheduling
            const istTime = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
            setCurrentTime(istTime);
        }, 1000); // Update every second for smooth countdown

        return () => clearInterval(timer);
    }, []);

    // Calculate next report time with live countdown
    const getNextReportTime = () => {
        const now = currentTime; // Use current time state for live updates
        const nextDaily = new Date(now);
        nextDaily.setHours(schedule.dailyHour, schedule.dailyMinute, 0, 0);

        // If the scheduled time has passed today, move to tomorrow
        if (now.getHours() > schedule.dailyHour ||
            (now.getHours() === schedule.dailyHour && now.getMinutes() >= schedule.dailyMinute)) {
            nextDaily.setDate(nextDaily.getDate() + 1);
        }

        const diffMs = nextDaily.getTime() - now.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const diffSecs = Math.floor((diffMs % (1000 * 60)) / 1000);

        return {
            date: nextDaily.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }),
            time: nextDaily.toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Asia/Kolkata'
            }),
            countdown: `${diffHours}h ${diffMins}m ${diffSecs}s`
        };
    };

    const nextReport = getNextReportTime();

    // Fetch admin emails
    const { data: adminsData, isLoading: adminsLoading } = useQuery(
        ['adminEmails', currentWorkspace?.id],
        async () => {
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const response = await apiClient.get('/api/reports/admins', { headers });
            return response.data;
        },
        {
            enabled: !!currentWorkspace?.id, // Only run query when workspace is available
            onError: (error) => {
                console.error('Error fetching admin emails:', error);
            }
        }
    );

    // Fetch schedule settings
    useQuery(
        ['scheduleSettings', currentWorkspace?.id],
        async () => {
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const response = await apiClient.get('/api/schedule-settings', { headers });
            return response.data;
        },
        {
            enabled: !!currentWorkspace?.id,
            onSuccess: (data) => {
                if (data) {
                    setSchedule({
                        dailyHour: data.dailyHour,
                        dailyMinute: data.dailyMinute,
                        weeklyHour: data.weeklyHour,
                        weeklyMinute: data.weeklyMinute,
                        weeklyDay: data.weeklyDay
                    });
                }
            },
            onError: (error) => {
                console.error('Error fetching schedule settings:', error);
            }
        }
    );

    // Send report mutation
    const sendReportMutation = useMutation(
        async ({ email, sendToAdmins }) => {
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const response = await apiClient.post('/api/reports/usage/send', {
                email,
                sendToAdmins
            }, { headers });
            return response.data;
        },
        {
            onSuccess: (data) => {
                toast.success(`Report sent successfully to ${data.details.successful} recipient(s)!`);
            },
            onError: (error) => {
                toast.error(error.response?.data?.details || 'Failed to send report');
            }
        }
    );

    // Preview report
    const handlePreview = () => {
        const token = localStorage.getItem('token');
        const url = `/api/reports/usage/preview`;

        // Open in new tab
        const previewWindow = window.open('', '_blank');

        fetch(apiClient.defaults.baseURL + url, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'x-workspace-id': currentWorkspace?.id || ''
            }
        })
            .then(response => response.text())
            .then(html => {
                previewWindow.document.write(html);
                previewWindow.document.close();
            })
            .catch(error => {
                console.error('Error previewing report:', error);
                toast.error('Failed to preview report');
                previewWindow.close();
            });
    };

    // Send to all admins
    const handleSendToAdmins = () => {
        sendReportMutation.mutate({ sendToAdmins: true });
    };

    // Save schedule settings mutation
    const saveScheduleMutation = useMutation(
        async (scheduleData) => {
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const response = await apiClient.put('/api/schedule-settings', scheduleData, { headers });
            return response.data;
        },
        {
            onSuccess: (data) => {
                toast.success('Schedule settings saved successfully!');
                setShowScheduleModal(false);
            },
            onError: (error) => {
                toast.error(error.response?.data?.error || 'Failed to save schedule settings');
            }
        }
    );

    // Handle save schedule
    const handleSaveSchedule = () => {
        setIsSavingSchedule(true);
        saveScheduleMutation.mutate(schedule, {
            onSettled: () => {
                setIsSavingSchedule(false);
            }
        });
    };


    const adminEmails = adminsData?.admins || [];
    const adminCount = adminEmails.length;

    return (
        <div className="space-y-6">
            {/* Header Card */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                    <div className="p-3 bg-purple-100 rounded-xl">
                        <Mail className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                        <h3 className="text-xl font-bold text-gray-900">📊 API Usage Email Reports</h3>
                        <p className="text-sm text-gray-600">Automated daily and weekly usage statistics via email</p>
                    </div>
                </div>
            </div>

            {/* Schedule Status & Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Schedule */}
                <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-blue-600" />
                            <h4 className="font-semibold text-gray-900">Report Schedule</h4>
                        </div>
                        <button
                            onClick={() => setShowScheduleModal(true)}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                        >
                            <Settings className="h-4 w-4" />
                            Configure
                        </button>
                    </div>

                    <div className="space-y-4">
                        {/* Daily Report */}
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-blue-600" />
                                    <span className="font-medium text-blue-900">Daily Report</span>
                                </div>
                                <span className="px-2 py-1 bg-blue-200 text-blue-900 rounded-full text-xs font-bold">
                                    Active
                                </span>
                            </div>
                            <p className="text-sm text-blue-800">
                                <strong>Every day at {schedule.dailyHour >= 12 ?
                                    (schedule.dailyHour === 12 ? '12' : schedule.dailyHour - 12) :
                                    (schedule.dailyHour === 0 ? '12' : schedule.dailyHour)}:{schedule.dailyMinute.toString().padStart(2, '0')} {schedule.dailyHour >= 12 ? 'PM' : 'AM'}</strong>
                            </p>
                            <p className="text-xs text-blue-600 mt-2">
                                ⏰ Next report in: <strong>{nextReport.countdown}</strong>
                            </p>
                        </div>

                        {/* Weekly Report */}
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-green-600" />
                                    <span className="font-medium text-green-900">Weekly Summary</span>
                                </div>
                                <span className="px-2 py-1 bg-green-200 text-green-900 rounded-full text-xs font-bold">
                                    Active
                                </span>
                            </div>
                            <p className="text-sm text-green-800">
                                <strong>Every {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][schedule.weeklyDay]} at {schedule.weeklyHour >= 12 ?
                                    (schedule.weeklyHour === 12 ? '12' : schedule.weeklyHour - 12) :
                                    (schedule.weeklyHour === 0 ? '12' : schedule.weeklyHour)}:{schedule.weeklyMinute.toString().padStart(2, '0')} {schedule.weeklyHour >= 12 ? 'PM' : 'AM'}</strong>
                            </p>
                        </div>

                        {/* Recipients */}
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="flex items-center gap-2 mb-2">
                                <Users className="h-4 w-4 text-purple-600" />
                                <span className="font-medium text-purple-900">Recipients</span>
                            </div>
                            {adminsLoading ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Loader className="h-3 w-3 animate-spin" />
                                    Loading...
                                </div>
                            ) : adminCount > 0 ? (
                                <>
                                    <p className="text-sm text-purple-800 mb-2">
                                        <strong>{adminCount} admin{adminCount !== 1 ? 's' : ''}</strong> will receive reports
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {adminEmails.slice(0, 3).map((email, idx) => (
                                            <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                                                {email}
                                            </span>
                                        ))}
                                        {adminCount > 3 && (
                                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                                                +{adminCount - 3} more
                                            </span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center gap-2 text-sm text-orange-600">
                                    <AlertCircle className="h-4 w-4" />
                                    <span>No recipients configured</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Send className="h-5 w-5 text-green-600" />
                        <h4 className="font-semibold text-gray-900">Quick Actions</h4>
                    </div>

                    <div className="space-y-4">
                        {/* Send to All Admins */}
                        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
                            <h5 className="font-semibold text-green-900 mb-2">Send to All Admins</h5>
                            <p className="text-sm text-green-700 mb-3">
                                Instantly send the latest usage report to all {adminCount || 'configured'} admin{adminCount !== 1 ? 's' : ''}
                            </p>
                            <button
                                onClick={handleSendToAdmins}
                                disabled={sendReportMutation.isLoading || adminCount === 0}
                                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-3 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all flex items-center justify-center gap-2 font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {sendReportMutation.isLoading ? (
                                    <>
                                        <Loader className="h-5 w-5 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-5 w-5" />
                                        Send Now to {adminCount} Admin{adminCount !== 1 ? 's' : ''}
                                    </>
                                )}
                            </button>
                        </div>


                        {/* Preview Button */}
                        <button
                            onClick={handlePreview}
                            className="w-full bg-white border-2 border-purple-300 text-purple-700 px-4 py-3 rounded-lg hover:bg-purple-50 transition-all flex items-center justify-center gap-2 font-medium"
                        >
                            <Eye className="h-5 w-5" />
                            Preview Report
                        </button>
                    </div>
                </div>
            </div>


            {/* Schedule Configuration Modal */}
            {showScheduleModal && (
                <ModalPortal>
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
                        <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-t-2xl p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
                                        <Settings className="h-6 w-6 text-white" />
                                    </div>
                                    <h2 className="text-xl font-bold text-white">Configure Report Schedule</h2>
                                </div>
                                <button
                                    onClick={() => setShowScheduleModal(false)}
                                    className="text-white hover:bg-white/20 rounded-lg p-2 transition-all"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="space-y-6">
                                {/* Daily Report Schedule */}
                                <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Calendar className="h-5 w-5 text-blue-600" />
                                        <h3 className="font-semibold text-blue-900">Daily Report</h3>
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Hour
                                            </label>
                                            <select
                                                value={schedule.dailyHour}
                                                onChange={(e) => setSchedule({ ...schedule, dailyHour: parseInt(e.target.value) })}
                                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                {Array.from({ length: 24 }, (_, i) => (
                                                    <option key={i} value={i}>
                                                        {i === 0 ? '12' : i < 12 ? `${i}` : i === 12 ? '12' : `${i - 12}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Minutes
                                            </label>
                                            <select
                                                value={schedule.dailyMinute}
                                                onChange={(e) => setSchedule({ ...schedule, dailyMinute: parseInt(e.target.value) })}
                                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                {Array.from({ length: 60 }, (_, i) => (
                                                    <option key={i} value={i}>
                                                        {i.toString().padStart(2, '0')}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                AM/PM
                                            </label>
                                            <select
                                                value={schedule.dailyHour >= 12 ? 'PM' : 'AM'}
                                                onChange={(e) => {
                                                    const isPM = e.target.value === 'PM';
                                                    const newHour = isPM ?
                                                        (schedule.dailyHour < 12 ? schedule.dailyHour + 12 : schedule.dailyHour) :
                                                        (schedule.dailyHour >= 12 ? schedule.dailyHour - 12 : schedule.dailyHour);
                                                    setSchedule({ ...schedule, dailyHour: newHour });
                                                }}
                                                className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="AM">AM</option>
                                                <option value="PM">PM</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Weekly Report Schedule */}
                                <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Calendar className="h-5 w-5 text-green-600" />
                                        <h3 className="font-semibold text-green-900">Weekly Summary</h3>
                                    </div>
                                    <div className="grid grid-cols-4 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Hour
                                            </label>
                                            <select
                                                value={schedule.weeklyHour}
                                                onChange={(e) => setSchedule({ ...schedule, weeklyHour: parseInt(e.target.value) })}
                                                className="w-full px-3 py-2 border-2 border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                            >
                                                {Array.from({ length: 24 }, (_, i) => (
                                                    <option key={i} value={i}>
                                                        {i === 0 ? '12' : i < 12 ? `${i}` : i === 12 ? '12' : `${i - 12}`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Min
                                            </label>
                                            <select
                                                value={schedule.weeklyMinute}
                                                onChange={(e) => setSchedule({ ...schedule, weeklyMinute: parseInt(e.target.value) })}
                                                className="w-full px-3 py-2 border-2 border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                            >
                                                {Array.from({ length: 60 }, (_, i) => (
                                                    <option key={i} value={i}>
                                                        {i.toString().padStart(2, '0')}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                AM/PM
                                            </label>
                                            <select
                                                value={schedule.weeklyHour >= 12 ? 'PM' : 'AM'}
                                                onChange={(e) => {
                                                    const isPM = e.target.value === 'PM';
                                                    const newHour = isPM ?
                                                        (schedule.weeklyHour < 12 ? schedule.weeklyHour + 12 : schedule.weeklyHour) :
                                                        (schedule.weeklyHour >= 12 ? schedule.weeklyHour - 12 : schedule.weeklyHour);
                                                    setSchedule({ ...schedule, weeklyHour: newHour });
                                                }}
                                                className="w-full px-3 py-2 border-2 border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                            >
                                                <option value="AM">AM</option>
                                                <option value="PM">PM</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Day
                                            </label>
                                            <select
                                                value={schedule.weeklyDay}
                                                onChange={(e) => setSchedule({ ...schedule, weeklyDay: parseInt(e.target.value) })}
                                                className="w-full px-3 py-2 border-2 border-green-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                            >
                                                <option value={1}>Monday</option>
                                                <option value={2}>Tuesday</option>
                                                <option value={3}>Wednesday</option>
                                                <option value={4}>Thursday</option>
                                                <option value={5}>Friday</option>
                                                <option value={6}>Saturday</option>
                                                <option value={0}>Sunday</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Info Notice */}
                                <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm text-yellow-800 mb-2">
                                                <strong>Note:</strong> Schedule settings are saved to the database and will be applied to automated reports.
                                            </p>
                                            <p className="text-xs text-yellow-700">
                                                The cron jobs will automatically use these settings for sending scheduled reports.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6 pt-4 border-t-2 border-gray-200">
                                <button
                                    onClick={() => setShowScheduleModal(false)}
                                    className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700 transition-all"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={handleSaveSchedule}
                                    disabled={isSavingSchedule}
                                    className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 flex items-center gap-2 font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSavingSchedule ? (
                                        <>
                                            <Loader className="h-5 w-5 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="h-5 w-5" />
                                            Save Schedule
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}
        </div>
    );
};

export default ApiUsageReportControl;

